import {Component, EventEmitter, Input, Output, OnDestroy, OnInit} from '@angular/core';
import {FormArray, FormControl, FormGroup, Validators} from '@angular/forms';
import { EmployeeService } from '../../../employees/services/employee.service';
import {
  debounceTime,
  catchError,
  filter,
  map, shareReplay, startWith,
  switchMap,
  take,
  tap,
  takeUntil
} from 'rxjs/operators';
import { SearchEmployeeData } from '../../../employees/domain/search-employee-data';
import { AssignEmployeeService } from '../../../services/assign-employee.service';
import { Profession } from '../../../employees/domain/profession';
import {BehaviorSubject, combineLatest, combineLatestWith, of, Subject, throwError} from 'rxjs';
import { MatSelectionListChange } from '@angular/material/list';
import { EmployeeConflict } from '../../models/employee-conflict';
import { EmployeeListItem } from '../../../employees/domain/employee-list';
import { EmployeesAssessmentsService } from '../../../projects/services/employees-assessments.service';
import { RatingForm } from '../../../projects/domain/employee-assessment-form';
import {CertificateNameService} from "../../../../core/certificate-name.service";
import {SkillGroupService} from "../../../employees/services/skill-group.service";
import {StatusesService} from "../../../employees/services/statuses.service";
import {VisaTypeService} from "../../../services/visa-type.service";
import { EmployeeWarningStatus } from '../../employee-conflicts-tooltip/employee-conflicts-tooltip.component';
import {PhaseWithEmployees} from "../../../projects/domain/phase-with-employees";
import { FinancialProtection, protectedRange } from '../../models/financial-protection';
import { ProjectService } from '../../../services/project.service';
import moment from 'moment';

interface AssignmentAvailability {
  state: 'checking' | 'available' | 'blocked' | 'unavailable';
  message: string;
  label?: string;
}

interface AssignmentCandidate extends EmployeeListItem {
  assignmentAvailability: AssignmentAvailability;
}

interface EmployeeFiltersForm {
  search: FormControl<string>;
  availableOnly: FormControl<boolean>,
  professionId: FormControl<number | null>,
  professionsIds: FormControl<number[]>,
  statuses: FormControl<string[]>,
  skillIds: FormControl<number[]>,
  certificateIds: FormControl<number[]>,
  visaTypeIds: FormControl<number[]>,
  ratings: FormArray<FormControl<number>>,
}

interface EmployeeFiltersValue {
  search: string;
  availableOnly: boolean,
  professionId: number | null,
  professionsIds:number[],
  statuses: string[],
  skillIds:number[],
  certificateIds: number[],
  visaTypeIds: number[],
  ratings: Array<number | null>,
}

@Component({
  selector: 'app-employees-step',

  standalone: false,
  templateUrl: 'employees-step.component.html',
  styleUrls: ['../employee-assign.scss'],
  styles: [`
    .assignment-option.mat-mdc-list-item { height: auto; min-height: 72px; padding-top: 12px; padding-bottom: 12px; }
    .assignment-option .assignment-message.mat-mdc-list-item-line {
      white-space: normal; overflow: visible; overflow-wrap: anywhere; line-height: 1.4; opacity: 1;
    }
    .assignment-blocked { color: #b71c1c; }
    .assignment-option .assignment-warning { color: #a15c00; }
  `],
})
export class EmployeesStepComponent implements OnDestroy, OnInit {
  @Input() isPreviousStep: boolean;
  @Input() professions: Profession[];
  @Input() projectId: string;
  private phaseSource = new BehaviorSubject<PhaseWithEmployees | null>(null);
  private currentPhase: PhaseWithEmployees;
  @Input() set phase(value: PhaseWithEmployees) {
    if (value?.id !== this.phaseSource.value?.id) {
      this.selectedEmployeeIds.clear();
      this.availabilityByEmployee.clear();
      this.protectionByEmployee.clear();
      this.employeeForm.controls.employeeId.setValue([]);
    }
    this.currentPhase = value;
    this.closeConflictsPopover();
    this.lastEmployeeConflicts = [];
    this.phaseSource.next(value);
  }
  get phase(): PhaseWithEmployees { return this.currentPhase; }

  @Output() closeEmployeeAssign = new EventEmitter<void>();
  @Output() backToPreviousStep = new EventEmitter<void>();
  @Output() employeeSelected = new EventEmitter<string[]>();

  employeeFilterForm: FormGroup<EmployeeFiltersForm> = new FormGroup({
    search: new FormControl<string>('', { nonNullable: true }),
    availableOnly: new FormControl<boolean>(false, { nonNullable: true }),
    professionId: new FormControl<number | null>(null),
    professionsIds: new FormControl<number[]>([]),
    statuses: new FormControl<string[]>([]),
    skillIds: new FormControl<number[]>([]),
    certificateIds: new FormControl<number[]>([]),
    visaTypeIds: new FormControl<number[]>([]),
    ratings: new FormArray<FormControl<number>>([]),
  });

  employeeForm = new FormGroup({
    employeeId: new FormControl<string[]>([], Validators.required),
  });

  private currentEmployees: AssignmentCandidate[] = [];

  private selectedEmployeeIds = new Set<string>();
  private availabilityByEmployee = new Map<string, AssignmentAvailability>();
  private protectionByEmployee = new Map<string, FinancialProtection>();
  private refreshCandidates$ = new Subject<void>();
  private destroy$ = new Subject<void>();
  candidatesLoading = true;
  candidatesError = '';

  readonly employees$ = this.employeeFilterForm.valueChanges.pipe(
    debounceTime(300),
    filter(() => this.employeeFilterForm.valid),
    startWith(this.employeeFilterForm.getRawValue()),
    combineLatestWith(this.phaseSource, this.refreshCandidates$.pipe(startWith(undefined))),
    filter(([, phase]) => !!phase),
    switchMap(([filterFormValue, phase]) => {
      this.closeConflictsPopover();
      this.candidatesLoading = true;
      this.candidatesError = '';
      this.currentEmployees = [];
      let checkedPhase = phase;
      return this.projectService.getProjectPhasesWithEmployees(this.projectId, true).pipe(
        switchMap(phases => {
          checkedPhase = phases.find(current => current.id === phase.id);
          if (!checkedPhase) return throwError(() => new Error('Phase unavailable'));
          checkedPhase = { ...checkedPhase, requiredProfessions: phase.requiredProfessions };
          this.currentPhase = checkedPhase;
          return this.employeeService.getEmployeeList(this.mapToSearchEmployeeData(filterFormValue));
        }),
        map((res) => res.items),
        map((employees) =>
          filterFormValue.availableOnly
            ? employees.filter((emp) => emp.isAvailable)
            : employees
        ),
        map((employees) => employees.filter((emp) => emp.status.status !== 'Blocked')),
        switchMap((employees: EmployeeListItem[]) => {
          this.candidatesLoading = false;
          this.conflicts$.next([]);
          this.lastEmployeeConflicts = [];
          if (!employees.length) return of([] as AssignmentCandidate[]);
          return combineLatest(employees.map(employee => this.employeeService.getEmployee(employee.id, true).pipe(
            map(details => {
              this.protectionByEmployee.set(employee.id, details.financialProtection);
              return { ...employee, assignmentAvailability: this.assignmentAvailability(employee.id, details.financialProtection, checkedPhase) };
            }),
            catchError(() => of({ ...employee, assignmentAvailability: this.unavailableAssignment() })),
            startWith({ ...employee, assignmentAvailability: { state: 'checking' as const, label: 'Checking availability…', message: 'Checking available work dates…' } }),
          )));
        }),
        tap(employees => {
          this.currentEmployees = employees;
          for (const employee of employees) {
            this.availabilityByEmployee.set(employee.id, employee.assignmentAvailability);
            if (employee.assignmentAvailability.state === 'blocked' || employee.assignmentAvailability.state === 'unavailable') {
              this.selectedEmployeeIds.delete(employee.id);
            }
          }
          this.employeeForm.controls.employeeId.setValue(Array.from(this.selectedEmployeeIds));
        }),
        catchError(() => {
          this.candidatesLoading = false;
          this.candidatesError = 'We couldn’t load the employee list. Select Check again to retry.';
          return of([] as AssignmentCandidate[]);
        }),
        startWith([] as AssignmentCandidate[]),
      );
    }),
    takeUntil(this.destroy$),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  hoveredEmployeeId: string | null = null;
  hoveredProfessionId: number | null = null;
  isConflictsPopoverOpen = false;
  currentPopoverWarningStatus: EmployeeWarningStatus | null = null;
  private dismissedPopoverEmployeeId: string | null = null;
  private lastEmployeeConflicts: { employeeId: string; conflicts: EmployeeConflict[] }[] = [];
  conflicts$ = new BehaviorSubject<EmployeeConflict[]>([]);
  readonly skillGroups$ = this.skillGroupService.skillGroupsAllWithRemoved$.pipe(
    map(skillGroups => skillGroups
      .filter(skillGroup => skillGroup.skills && skillGroup.skills.length > 0)
      .map(skillGroup => ({
        ...skillGroup,
        name: skillGroup.isRemoved ? `${skillGroup.name} (Removed)` : skillGroup.name,
        skills: skillGroup.skills.map(skill => ({
          ...skill,
          name: skill.isRemoved ? `${skill.name} (Removed)` : skill.name
        }))
      }))
    )
  );

  readonly statuses$ = this.statusesService.statuses$;

  readonly certificates$ = this.certificateService.allWithRemovedCertificates$.pipe(
    map(certificates => certificates.map(certificate => ({
      ...certificate,
      name: certificate.isRemoved ? `${certificate.name} (Removed)` : certificate.name
    })))
  );
  readonly visaTypes$ = this.visaTypeService.allWithRemovedOrUsedVisas$.pipe(
    map(visaTypes => visaTypes.map(visaType => ({
      ...visaType,
      name: visaType.isRemoved ? `${visaType.name} (Removed)` : visaType.name
    })))
  );

  get ratingControlsArray(): FormArray<FormControl<number>> {
    return this.employeeFilterForm.controls.ratings;
  }

  constructor(
    private readonly employeeService: EmployeeService,
    private readonly assignEmployeeService: AssignEmployeeService,
    private readonly projectService: ProjectService,
    private readonly assessmentsService: EmployeesAssessmentsService,
    private statusesService: StatusesService,
    private certificateService: CertificateNameService,
    private skillGroupService: SkillGroupService,
    private visaTypeService: VisaTypeService,
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.employeeFilterForm.controls.professionId.valueChanges
      .pipe(
        tap((professionId) => {
          if (professionId) {
            const requiredProfession = this.phase.requiredProfessions?.find(
              profession => profession.professionId === professionId
            );

            if (requiredProfession) {
              const certificateIds = requiredProfession.requiredCertificates?.map(cert => cert.id) || [];
              const skillIds = requiredProfession.requiredSkills?.map(skill => skill.id) || [];

              this.employeeFilterForm.patchValue({
                certificateIds: certificateIds,
                skillIds: skillIds
              }, { emitEvent: false });
            }
          } else {
            this.employeeFilterForm.patchValue({
              certificateIds: [],
              skillIds: []
            }, { emitEvent: false });
          }
        }),
        takeUntil(this.destroy$)
      ).subscribe();
  }

  findRequiredSkillsOrCertificatesByProfessionId(professionId: number, requiredType: 'skills' | 'certificates'): any[] {
    if (!this.phase || !this.phase.requiredProfessions) {
      return [];
    }

    const requiredProfession = this.phase.requiredProfessions.find(rp => rp.professionId === professionId);
    if (!requiredProfession) {
      return [];
    }
    if ('skills' === requiredType && requiredProfession.requiredSkills) {
      return requiredProfession.requiredSkills;
    }

    if ('certificates' === requiredType && requiredProfession.requiredCertificates) {
      return requiredProfession.requiredCertificates;
    }

    return [];
  }

  onEmployeeAssignClose(): void {
    this.closeEmployeeAssign.emit();
  }

  goBackToPreviousStep(): void {
    this.backToPreviousStep.emit();
  }

  onEmployeeSelect(): void {
    const selectedIds = Array.from(this.selectedEmployeeIds).filter(id => this.availabilityByEmployee.get(id)?.state === 'available'
      && this.assignmentAvailability(id, this.protectionByEmployee.get(id), this.phase).state === 'available');
    this.selectedEmployeeIds = new Set(selectedIds);
    this.employeeForm.controls.employeeId.setValue(selectedIds);
    if (this.canAssign) this.employeeSelected.emit(selectedIds);
  }

  get canAssign(): boolean {
    return !this.candidatesLoading && !this.candidatesError && this.selectedEmployeeIds.size > 0
      && Array.from(this.selectedEmployeeIds).every(id => this.availabilityByEmployee.get(id)?.state === 'available');
  }

  refreshCandidates(): void { this.refreshCandidates$.next(); }

  private unavailableAssignment(): AssignmentAvailability {
    return { state: 'unavailable', message: 'We couldn’t check this employee’s available work dates. Select Check again before assigning them.' };
  }

  private assignmentAvailability(employeeId: string, protection: FinancialProtection | undefined, phase: PhaseWithEmployees): AssignmentAvailability {
    if (!Array.isArray(protection?.periods) || !Array.isArray(protection?.months) || !Array.isArray(phase.employees)) return this.unavailableAssignment();
    let start = phase.startDate;
    const end = phase.endDate;
    if (start && end) {
      for (const period of protection.months) {
        if (phase.startDate <= period.to && end >= period.from) {
          const nextStart = moment(period.to).add(1, 'day').format('YYYY-MM-DD');
          if (nextStart > start) start = nextStart;
        }
      }
      if (start > end) return { state: 'blocked', label: 'No available work dates', message: 'No available work dates remain in this phase after the protected financial period. Choose a later phase, or contact Finance.' };
    } else if (protectedRange(protection, start, end)) {
      return { state: 'blocked', label: 'Protected financial period', message: 'This phase overlaps a protected financial period. Choose phase dates in an open period, or contact Finance.' };
    }
    const overlap = phase.employees.find(employee => employee.employeeId === employeeId
      && (start || '0001-01-01') <= (employee.endWork || phase.endDate || '9999-12-31')
      && (end || '9999-12-31') >= (employee.startWork || phase.startDate || '0001-01-01'));
    if (overlap) {
      return { state: 'blocked', label: 'Dates overlap existing assignment', message: `These dates overlap this employee’s existing assignment to this phase for ${overlap.startWork || phase.startDate || 'an open start'} – ${overlap.endWork || phase.endDate || 'an open end'}. Another assignment is allowed when the dates do not overlap. Use Adjust to change the existing assignment.` };
    }
    if (start !== phase.startDate) {
      return {
        state: 'available',
        label: `Dates adjusted: ${start} – ${end}`,
        message: `This employee will be assigned for ${start} – ${end}. The start moves from ${phase.startDate} to ${start} because of protected financial records.`,
      };
    }
    return { state: 'available', message: '' };
  }

  get filteredProfessions(): Profession[] {
    if (this.phase && this.phase.requiredProfessions && this.phase.requiredProfessions.length > 0) {
      const requiredIds = this.phase.requiredProfessions.map(rp => rp.professionId);
      return this.professions?.filter(profession => requiredIds.includes(profession.id));
    }

    return this.professions;
  }

  onEmployeeSelectionChange(event: MatSelectionListChange) {
    const option = event.options[0];
    const empId = option.value as string;

    if (option.selected && this.availabilityByEmployee.get(empId)?.state === 'available') {
      this.selectedEmployeeIds.add(empId);
    } else {
      option.selected = false;
      this.selectedEmployeeIds.delete(empId);
    }

    this.employeeForm.patchValue({
      employeeId: Array.from(this.selectedEmployeeIds),
    });
  }

  onProfessionMouseEnter(professionId: number): void {
    this.hoveredProfessionId = professionId;
  }

  onProfessionMouseLeave(): void {
    this.hoveredProfessionId = null;
  }

  openConflictsPopover(employeeId: string) {
    if (this.isConflictsPopoverOpen || this.dismissedPopoverEmployeeId === employeeId) return;
    this.isConflictsPopoverOpen = true;
    this.hoveredEmployeeId = employeeId;
    this.conflicts$.next([]);

    const employee = this.currentEmployees.find((emp) => emp.id === employeeId);
    if (employee?.warningStatus) {
      this.currentPopoverWarningStatus = {
        status: employee.status.status,
        expirationDate: employee.status.expirationDate,
      };
    } else {
      this.currentPopoverWarningStatus = null;
    }

    if (!employee || employee.isAvailable) return;

    const existing = this.lastEmployeeConflicts.find((item) => item.employeeId === employeeId);
    if (existing) {
      this.conflicts$.next(existing.conflicts);
      return;
    }

    const phase = this.phase;
    this.assignEmployeeService.checkConflictsWithProject(this.projectId, phase.id, [employeeId])
      .pipe(take(1))
      .subscribe({
        next: (conflicts) => {
          if (this.phase !== phase) return;
          this.lastEmployeeConflicts.push({ employeeId, conflicts });
          if (this.hoveredEmployeeId === employeeId) this.conflicts$.next(conflicts);
        },
        error: (err) => {
          if (this.phase !== phase) return;
          const conflicts = Array.isArray(err.error) ? err.error : [];
          this.lastEmployeeConflicts.push({ employeeId, conflicts });
          if (this.hoveredEmployeeId === employeeId) this.conflicts$.next(conflicts);
        }
      });
  }

  closeConflictsPopover() {
    this.hoveredEmployeeId = null;
    this.isConflictsPopoverOpen = false;
    this.dismissedPopoverEmployeeId = null;
  }

  dismissConflictsPopover() {
    const employeeId = this.hoveredEmployeeId;
    this.closeConflictsPopover();
    this.dismissedPopoverEmployeeId = employeeId;
  }

  allowConflictsPopover(employeeId: string) {
    if (this.dismissedPopoverEmployeeId === employeeId) this.dismissedPopoverEmployeeId = null;
  }

  employeeIdTracker(index: number, item: EmployeeListItem): string {
    return item.id;
  }

  professionIdTracker(index: number, item: Profession): number {
    return item.id;
  }

  private mapToSearchEmployeeData(
    filterFormValue: Partial<EmployeeFiltersValue>
  ): Partial<SearchEmployeeData> {
    const mappedRatings: RatingForm[] = (filterFormValue.ratings || []).map((rating, index) => ({
      value: rating,
      categoryId: index,
    }));

    return {
      pageIndex: 0,
      pageSize: 8,
      search: filterFormValue.search ? filterFormValue.search : null,
      professionId: filterFormValue.professionId,
      professionsIds: filterFormValue.professionsIds,
      skillIds: filterFormValue.skillIds,
      certificateIds: filterFormValue.certificateIds,
      visaTypeIds: filterFormValue.visaTypeIds,
      statuses: filterFormValue.statuses,
      availableFrom: this.phase.startDate,
      availableTo: this.phase.endDate,
      ratings: mappedRatings,
    };
  }
}
